/**
 * portfolio.ts — Portfolio Health Report frontend types + runtime guard.
 *
 * Mirrors packages/worker/src/services/portfolio-rollup.ts's `PortfolioRollup`
 * shape exactly (camelCase — a JS object serialised into a jsonb column, not
 * a DataStore-style column name, so no snake_case conversion is involved).
 *
 * `parseRollup` is the frontend's only defence against a malformed or
 * unexpected JSON blob reaching the render layer — the backend types the
 * `rollup_data` column as `unknown` deliberately, so the frontend must never
 * assume its shape without validating it first. It never throws, and always
 * returns either `null` (input was not a plain object) or a fully-formed
 * `PortfolioRollup` with defensive defaults for every field.
 */

// ---------------------------------------------------------------------------
// Rollup shape
// ---------------------------------------------------------------------------

export interface PortfolioKpis {
	totalTasksOpen: number;
	totalTasksOverdue: number;
	totalTasksCompletedThisWeek: number;
	totalMilestonesOverdue: number;
}

export interface PortfolioSentimentResult {
	label: string;
	score?: number;
	rationale?: string;
	signals?: string[];
	emailsAnalysed: number;
}

export interface PortfolioProjectCard {
	projectId: string;
	projectName: string;
	metrics: unknown;
	deltas: unknown | null;
	sentiment: PortfolioSentimentResult;
	scope: string | null;
}

export interface PortfolioSkippedProject {
	projectId: string;
	projectName: string;
	reason: string;
}

export interface PortfolioRollup {
	generatedAt: string;
	projectsTotal: number;
	projectsIncluded: number;
	kpis: PortfolioKpis;
	projects: PortfolioProjectCard[];
	skipped: PortfolioSkippedProject[];
}

// ---------------------------------------------------------------------------
// Raw API response shape — mirrors DbPortfolioReport
// (packages/worker/src/services/supabase.ts). Returned unwrapped by
// GET /api/portfolio/report/latest.
// ---------------------------------------------------------------------------

export interface PortfolioReportResponse {
	id: string;
	report_date: string;
	fortnight_index: number | null;
	projects_included: number;
	rollup_data: unknown;
	ai_narrative: string | null;
	session_id: string | null;
	generation_status: string;
	error_message: string | null;
	created_at: string;
	updated_at: string;
}

// ---------------------------------------------------------------------------
// Defensive parsing helpers
// ---------------------------------------------------------------------------

/** True for non-null, non-array objects — the only shape we treat as "a record". */
function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toSafeString(value: unknown, fallback = ""): string {
	return typeof value === "string" ? value : fallback;
}

function toSafeNumber(value: unknown, fallback = 0): number {
	return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function parseKpis(value: unknown): PortfolioKpis {
	const source = isPlainObject(value) ? value : {};
	return {
		totalTasksOpen: toSafeNumber(source.totalTasksOpen),
		totalTasksOverdue: toSafeNumber(source.totalTasksOverdue),
		totalTasksCompletedThisWeek: toSafeNumber(source.totalTasksCompletedThisWeek),
		totalMilestonesOverdue: toSafeNumber(source.totalMilestonesOverdue),
	};
}

function parseSentiment(value: unknown): PortfolioSentimentResult {
	const source = isPlainObject(value) ? value : {};
	const result: PortfolioSentimentResult = {
		label: toSafeString(source.label),
		emailsAnalysed: toSafeNumber(source.emailsAnalysed),
	};
	if (typeof source.score === "number" && Number.isFinite(source.score)) {
		result.score = source.score;
	}
	if (typeof source.rationale === "string") {
		result.rationale = source.rationale;
	}
	if (Array.isArray(source.signals)) {
		result.signals = source.signals.filter((s): s is string => typeof s === "string");
	}
	return result;
}

/** Drops garbage entries (anything that isn't a plain object) rather than crashing the parse. */
function parseProjectCard(value: unknown): PortfolioProjectCard | null {
	if (!isPlainObject(value)) return null;
	return {
		projectId: toSafeString(value.projectId),
		projectName: toSafeString(value.projectName),
		metrics: value.metrics,
		deltas: value.deltas === undefined ? null : value.deltas,
		sentiment: parseSentiment(value.sentiment),
		scope: typeof value.scope === "string" ? value.scope : null,
	};
}

function parseSkippedProject(value: unknown): PortfolioSkippedProject | null {
	if (!isPlainObject(value)) return null;
	return {
		projectId: toSafeString(value.projectId),
		projectName: toSafeString(value.projectName),
		reason: toSafeString(value.reason),
	};
}

/**
 * Parses an `unknown` value (the raw `rollup_data` jsonb column) into a
 * well-formed `PortfolioRollup`, or `null` when the input is not a plain
 * object at all (null, undefined, an array, or a primitive).
 *
 * Never throws — any unexpected shape inside a plain object is defaulted to
 * a safe value rather than propagated or allowed to crash the render layer.
 */
export function parseRollup(input: unknown): PortfolioRollup | null {
	try {
		if (!isPlainObject(input)) return null;

		const projectsRaw = Array.isArray(input.projects) ? input.projects : [];
		const skippedRaw = Array.isArray(input.skipped) ? input.skipped : [];

		return {
			generatedAt: toSafeString(input.generatedAt),
			projectsTotal: toSafeNumber(input.projectsTotal),
			projectsIncluded: toSafeNumber(input.projectsIncluded),
			kpis: parseKpis(input.kpis),
			projects: projectsRaw
				.map(parseProjectCard)
				.filter((p): p is PortfolioProjectCard => p !== null),
			skipped: skippedRaw
				.map(parseSkippedProject)
				.filter((s): s is PortfolioSkippedProject => s !== null),
		};
	} catch {
		return null;
	}
}
